// 国企模块页面访问日志上报（进入 / 心跳 / 离开）
// 轻量 mixin：页面在 data 中定义 pageName（页面名称，如“合规自查表”）即可自动上报
// 进入：POST /bigdata/userPageAccess/entry          —— 返回 recordId
// 心跳：GET  /bigdata/userPageAccess/report?id={recordId}&action=2
// 离开：GET  /bigdata/userPageAccess/report?id={recordId}&action=3
import axios from 'axios'

// 心跳间隔（毫秒），后端同时以最后一次心跳做超时兜底判断离开
const HEARTBEAT_INTERVAL = 30 * 1000

// 生成 UUID（RFC4122 v4 形式）
function genUUID () {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// 访客唯一标识：localStorage 持久化，用于 UV 去重
function getVisitorId () {
  let id = localStorage.getItem('visitorId')
  if (!id) {
    id = genUUID()
    localStorage.setItem('visitorId', id)
  }
  return id
}

// 设备类型：PC / PHONE / TABLET
function getDeviceType (ua) {
  if (/iPad|Tablet|PlayBook/i.test(ua)) return 'TABLET'
  if (/Mobile|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'PHONE'
  return 'PC'
}

// 操作系统
function getOS (ua) {
  if (/Windows NT 10/i.test(ua)) return 'Windows 10'
  if (/Windows NT 6.3/i.test(ua)) return 'Windows 8.1'
  if (/Windows NT 6.2/i.test(ua)) return 'Windows 8'
  if (/Windows NT 6.1/i.test(ua)) return 'Windows 7'
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Mac OS X/i.test(ua)) return 'macOS'
  if (/Linux/i.test(ua)) return 'Linux'
  return ''
}

// 浏览器
function getBrowser (ua) {
  if (/MicroMessenger/i.test(ua)) return 'WeChat'
  if (/Edg\//i.test(ua)) return 'Edge'
  if (/Chrome\//i.test(ua)) return 'Chrome'
  if (/Safari\//i.test(ua)) return 'Safari'
  if (/Firefox\//i.test(ua)) return 'Firefox'
  if (/MSIE|Trident/i.test(ua)) return 'IE'
  return ''
}

// 下载日志上报：客户点击下载时调用
// pageName 页面名称、filename 下载文件名、url 下载文件地址
export function reportDownload ({ openId, pageName, filename, url }) {
  const ua = navigator.userAgent
  axios
    .post('/crmapi/bigdata/userPageAccess/download', {
      openId: openId || '',
      userAgent: ua,
      deviceType: getDeviceType(ua),
      os: getOS(ua),
      browser: getBrowser(ua),
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      visitorId: getVisitorId(),
      pageName: pageName || '',
      filename: filename || '',
      url: url || ''
    })
    .catch(() => {})
}

export default {
  mounted () {
    this.pageAccessEntry()
    this.pageAccessBind()
  },
  beforeDestroy () {
    this.pageAccessUnbind()
    this.pageAccessLeave()
  },
  methods: {
    // 进入页面：上报并拿到 recordId，随后开启心跳
    pageAccessEntry () {
      const ua = navigator.userAgent
      axios
        .post('/crmapi/bigdata/userPageAccess/entry', {
          openId: this.$cookies.get('openId') || '',
          visitorId: getVisitorId(),
          pageName: this.pageName || (this.$route.meta && this.$route.meta.title) || '',
          url: window.location.href,
          refererUrl: document.referrer || '',
          userAgent: ua,
          deviceType: getDeviceType(ua),
          os: getOS(ua),
          browser: getBrowser(ua),
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        })
        .then((res) => {
          const data = res && res.data
          if (data && data.result) {
            this._pageAccessRecordId = data.result
            this.pageAccessStartHeartbeat()
          }
        })
        .catch(() => {})
    },
    // 开启心跳定时上报
    pageAccessStartHeartbeat () {
      this.pageAccessStopHeartbeat()
      this._pageAccessTimer = setInterval(() => {
        this.pageAccessReport(2)
      }, HEARTBEAT_INTERVAL)
    },
    pageAccessStopHeartbeat () {
      if (this._pageAccessTimer) {
        clearInterval(this._pageAccessTimer)
        this._pageAccessTimer = null
      }
    },
    // 上报：action 2 心跳、3 离开
    pageAccessReport (action) {
      if (!this._pageAccessRecordId) return
      axios
        .get('/crmapi/bigdata/userPageAccess/report', {
          params: { id: this._pageAccessRecordId, action }
        })
        .catch(() => {})
    },
    // 离开页面：停止心跳并上报一次离开
    pageAccessLeave () {
      this.pageAccessStopHeartbeat()
      if (this._pageAccessRecordId && !this._pageAccessReported) {
        this._pageAccessReported = true
        this.pageAccessReport(3)
      }
    },
    // 监听离开：页面隐藏（切后台 / 切 Tab）与页面卸载
    pageAccessBind () {
      this._pageAccessOnHide = () => {
        if (document.visibilityState === 'hidden') this.pageAccessLeave()
      }
      this._pageAccessOnPageHide = () => this.pageAccessLeave()
      document.addEventListener('visibilitychange', this._pageAccessOnHide)
      window.addEventListener('pagehide', this._pageAccessOnPageHide)
    },
    pageAccessUnbind () {
      if (this._pageAccessOnHide) {
        document.removeEventListener('visibilitychange', this._pageAccessOnHide)
        this._pageAccessOnHide = null
      }
      if (this._pageAccessOnPageHide) {
        window.removeEventListener('pagehide', this._pageAccessOnPageHide)
        this._pageAccessOnPageHide = null
      }
    }
  }
}
